import React, { useState, useEffect } from 'react';
import { FiBookOpen, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import SolveQuestions from '../SolveQuestions';
import FlipCardWrapper from '../FlipCardWrapper';
import ConceptToCharacteristic from '../ConceptToCharacteristic';
import ConceptMatchingGame from '../ConceptMatchingGame';
import FlashcardQuiz from '../flashcard-quiz';
import { useClient } from 'jsonapi-react';
import MindMap from '@/components/custom/mind-map';
import { ContentCarousel } from '@/components/custom/content-carousel';
import { ScormQuizCards } from '../flashcard-quiz/scorm-quiz-cards';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { GuideBlockArticle, GuideBlockArticleContent, GuideBlockArticleFooter, GuideBlockArticleHat, GuideBlockArticleHeader, GuideBlockArticleTitle } from '@/components/custom/guide';
import { isPlayfulTheme } from '@/lib/layout-version';
import tiaCursos from '@/assets/images/tia/TIA-CURSOS.png';
import { TypewriterText } from '@/components/custom/typewriter-text';

// Remove o código de descritor "Dx" no início OU fim, com qualquer separador (: · — – -).
const stripCode = text => (text || '').replace(/^D\d+\s*[:·—–-]\s*/i, '').replace(/\s*[:·—–-]\s*D\d+\s*$/i, '');

// Primeira frase (fallback conciso p/ o jogo de pareamento quando não há `definition`).
const firstSentence = text => {
  const trimmed = (text || '').trim();
  const match = trimmed.match(/^.*?[.!?](\s|$)/);
  const first = (match ? match[0] : trimmed).trim();
  return first.length > 140 ? `${first.slice(0, 137).trimEnd()}…` : first;
};

// Remove o nome do conceito do INÍCIO do significado (senão o pareamento entrega a resposta).
const hideConcept = (text, concept) => {
  const value = (text || '').trim();
  const name = (concept || '').trim();
  if (!name || !value.toLowerCase().startsWith(name.toLowerCase())) return value;
  const rest = value.slice(name.length).replace(/^[\s:–—-]+/, '');
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : value;
};

// Título amigável da seção: sem código "Dx"; avaliação ganha nome convidativo.
const sectionTitle = scorm => {
  if (scorm.scorm_type === 'questions') return 'Hora de praticar!';
  if (scorm.scorm_type === 'quiz_cards') return 'Quiz rápido';
  return stripCode(scorm.content?.title);
};

export function StudyGuideBlockArticle({ scorm, study_plan_video_id, endpoints, backRoute = '/guia-de-estudos', last, allSectionsDone, setActiveSectionIndex, activeSectionIndex, setSections, setFinished }) {
  const navigate = useNavigate();
  const [goNext, setGoNext] = useState(false);
  const [scormQuestions, setScormQuestions] = useState([]);
  const client = useClient();
  const [loading, setLoading] = useState(false);

  const setScormSectionDone = async scormId => {
    const response = await client.mutate(endpoints.sectionDone(scormId), { done: true }, { method: 'put' });
    return response;
  };

  const nextSection = scornId => {
    setLoading(true);
    setScormSectionDone(scornId);

    setSections(prevSections => {
      return prevSections.map((section, index) => (index === activeSectionIndex ? { ...section, done: true } : section));
    });

    setActiveSectionIndex(activeSectionIndex + 1);
    setLoading(false);
  };

  const getScormQuestions = async () => {
    let url = endpoints.scormQuestions;
    if (scorm.scorm.scorm_type === 'questions') {
      url = url.concat(`?type=questions`);
    } else if (scorm.scorm.scorm_type === 'learning_section') {
      url = url.concat(`?type=learning_section`);
    } else {
      url = '';
    }

    if (url !== '') {
      const response = await client.fetch(url);
      return response.data;
    }
  };

  const splitTopics = topics => {
    const conceptsData = topics.map(topic => ({
      id: topic.title.toLowerCase().replace(/\s+/g, ''),
      name: topic.title
    }));

    const meaningsData = topics.map(topic => ({
      id: topic.title.toLowerCase().replace(/\s+/g, ''),
      meaning: hideConcept(topic.definition || firstSentence(topic.content), topic.title)
    }));

    return { conceptsData, meaningsData };
  };

  useEffect(() => {
    getScormQuestions().then(data => {
      setScormQuestions(data);
    });
  }, [scorm]);

  useEffect(() => {
    function handleScroll() {
      const screenHeight = document.documentElement.scrollHeight;
      const endPage = window.scrollY + window.innerHeight;

      if (endPage === screenHeight) setGoNext(false);
      else setGoNext(true);
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [goNext]);

  const renderContent = data => {
    const scorm = data.scorm;
    switch (scorm.scorm_type) {
      case 'questions':
        return (
          <SolveQuestions
            data={scormQuestions}
            scorm={data}
            setScormSectionDone={setScormSectionDone}
            setFinished={setFinished}
            backRoute={backRoute}
          />
        );
      case 'educational_content':
        const { conceptsData, meaningsData } = splitTopics(scorm.content.main_topics);
        return (
          <>
            <p
              className="mb-3 text-lg font-semibold"
              style={{ color: 'var(--color-secondary-dark)' }}
            >
              {stripCode(scorm.content.main_title)}
            </p>

            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}

            <ConceptMatchingGame
              conceptsData={conceptsData}
              meaningsData={meaningsData}
            />
          </>
        );
      case 'introduction': {
        const playful = isPlayfulTheme();
        const paragraphs = (scorm.content.introduction || '')
          .split(/\n+/)
          .map(p => p.trim())
          .filter(Boolean);
        const summary = scorm.content.summary || paragraphs[0] || '';
        const highlights = scorm.content.highlights || [];
        return (
          <>
            <div
              className={`relative mb-6 flex items-center gap-4 rounded-2xl p-5 ${playful ? 'mt-20 pl-[155px]' : ''}`}
              style={{ background: 'linear-gradient(135deg, var(--color-secondary-lightest), var(--color-primary-lightest))' }}
            >
              {playful ? (
                <img
                  src={tiaCursos}
                  alt="T.I.A"
                  className="pointer-events-none absolute bottom-0 left-2 z-10 h-[150px] !w-auto max-w-none object-contain object-bottom"
                />
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-card">
                  <FiBookOpen className="h-7 w-7" />
                </span>
              )}
              <p className={`mb-0 text-sm font-semibold ${playful ? 'text-neutral-900' : 'text-primary-dark'}`}>{playful ? <TypewriterText text={'Vamos ao essencial deste tópico, rapidinho!'} /> : 'Vamos ao essencial deste tópico, rapidinho!'}</p>
            </div>

            <p className="mb-0 text-base leading-relaxed text-neutral-darker">{summary}</p>

            {highlights.length > 0 && (
              <ul className="mt-5 grid list-none gap-3 p-0 sm:grid-cols-2">
                {highlights.map((highlight, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2.5 rounded-xl border border-solid border-primary-lightest bg-white p-3"
                  >
                    <FiCheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                    <span className="text-sm leading-snug text-neutral-darker">{highlight}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        );
      }
      case 'mindmap':
        return (
          <>
            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}
            {scorm.content.main_nodes && <MindMap data={scorm.content.main_nodes} />}
            <br />
            <br />
            {scorm.content.conclusion.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}
          </>
        );
      case 'learning_section':
        return (
          <>
            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}
            <br />
            <br />
            <ConceptToCharacteristic interactive_challenges={scorm.content.interactive_challenges} />
            <br />
            <br />
            {scorm.content.final_reflection.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}
          </>
        );
      case 'learning_experience':
        return (
          <>
            <p
              className="mb-3 text-lg font-semibold"
              style={{ color: 'var(--color-secondary-dark)' }}
            >
              {stripCode(scorm.content.general_title)}
            </p>
            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}
            <FlipCardWrapper flip_cards={scorm.content.flip_cards} />
            <br />
            <br />
            {scorm.content.conclusion.split('\n').map((paragraph, index) => (
              <p
                className="mb-4"
                key={index}
              >
                {paragraph}
              </p>
            ))}
          </>
        );
      case 'flashcard_quiz':
        return (
          <FlashcardQuiz
            onComplete={results => {
              setScormSectionDone(data.id);
            }}
            limit={scorm.content?.limit || 10}
          />
        );
      case 'content_carousel':
        return (
          <>
            {scorm.content.introduction &&
              scorm.content.introduction.split('\n').map((paragraph, index) => (
                <p
                  className="mb-4"
                  key={index}
                >
                  {paragraph}
                </p>
              ))}
            <ContentCarousel slides={scorm.content.slides} />
          </>
        );
      case 'quiz_cards':
        return (
          <ScormQuizCards
            cards={scorm.content.cards}
            scorm={data}
            setScormSectionDone={setScormSectionDone}
            setFinished={setFinished}
            backRoute={backRoute}
          />
        );
      default:
        return <p className="mb-4">Tipo de conteúdo desconhecido.</p>;
    }
  };

  return (
    <GuideBlockArticle>
      {loading && <Loader />}

      {!loading && (
        <>
          {!(isPlayfulTheme() && scorm.scorm.scorm_type === 'introduction') && (
            <GuideBlockArticleHeader>
              {/* <GuideBlockArticleHat>Introdução</GuideBlockArticleHat> */}
              <GuideBlockArticleTitle>{sectionTitle(scorm.scorm)}</GuideBlockArticleTitle>
            </GuideBlockArticleHeader>
          )}

          <GuideBlockArticleContent>{renderContent(scorm)}</GuideBlockArticleContent>

          <GuideBlockArticleFooter>
            {!last && !allSectionsDone && (
              <button
                type="button"
                onClick={() => {
                  if (last) navigate(backRoute);
                  nextSection(scorm.id);
                }}
                className={`flex items-center gap-2 rounded-full ${isPlayfulTheme() ? 'bg-secondary' : 'bg-primary'} px-6 py-3 font-extrabold text-white shadow-[0_8px_20px_rgba(68,43,140,0.35)] transition hover:brightness-110 active:scale-[0.97]`}
              >
                Próxima seção
                <FiArrowRight className="h-5 w-5" />
              </button>
            )}
          </GuideBlockArticleFooter>
        </>
      )}
    </GuideBlockArticle>
  );
}
